import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, X } from "lucide-react";

// Shown over the homepage when the marketing unsubscribe redirects here with
// ?unsub=1. The unsubscribe itself already happened server-side; this is
// just the on-brand confirmation, and it auto-closes so the visitor is left on
// the live homepage (a real page view).
const AUTO_CLOSE_MS = 3000;

export default function UnsubscribedPopup() {
    const [params, setParams] = useSearchParams();
    const [open, setOpen] = useState(false);

    // Open when the flag is present, then immediately strip it from the URL so a
    // refresh or share doesn't re-trigger the popup.
    useEffect(() => {
        if (params.get("unsub") !== "1") return;
        setOpen(true);
        const next = new URLSearchParams(params);
        next.delete("unsub");
        setParams(next, { replace: true });
    }, [params, setParams]);

    const close = useCallback(() => setOpen(false), []);

    // Auto-close after the timer.
    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(close, AUTO_CLOSE_MS);
        return () => clearTimeout(t);
    }, [open, close]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/70 backdrop-blur-sm px-4"
            data-testid="unsubscribed-popup"
            onClick={close}
        >
            <div
                className="panel relative w-full max-w-md p-10 text-center shadow-lift animate-fade-up"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="absolute top-4 right-4 p-1 rounded-md text-dim hover:text-foreground transition-colors duration-150"
                >
                    <X size={16} />
                </button>
                <span className="grid place-items-center w-16 h-16 mx-auto rounded-full bg-pos/10 text-pos">
                    <CheckCircle2 size={32} />
                </span>
                <h1 className="font-display text-2xl font-bold mt-5">You've been unsubscribed</h1>
                <p className="mt-3 text-sm text-dim">You will no longer receive these emails from us.</p>
                <p className="mt-5 eyebrow text-dim">This message closes automatically…</p>
            </div>
        </div>
    );
}
